import React, { useEffect, useState, useCallback } from 'react';
import { DefaultButton, ModalWindow, Alert } from 'shared/ui';
import { NotificationApi } from 'shared/api/notification';
import { userStore } from 'entities/index';
import { observer } from 'mobx-react';
import styles from './business-vk-bind.module.scss';

type BindState = 'loading' | 'unbound' | 'bound' | 'binding' | 'unbinding';

export const BusinessVkBind: React.FC = observer(() => {
  const [state, setState] = useState<BindState>('loading');
  const [showModal, setShowModal] = useState(false);
  const [bindCode, setBindCode] = useState('');
  const [vkBotChatUrl, setVkBotChatUrl] = useState('');
  const [error, setError] = useState('');
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const handleCloseAlert = useCallback(() => setAlertMessage(null), []);
  const handleCopyCode = useCallback(async () => {
    if (!bindCode) return;
    try {
      await navigator.clipboard.writeText(bindCode);
      setAlertMessage('Код скопирован в буфер обмена!');
    } catch {
      setAlertMessage('Не удалось скопировать код');
    }
  }, [bindCode]);

  useEffect(() => {
    if (!userStore.accountId) return;
    NotificationApi.checkBusinessVkLink(userStore.accountId).then(
      (res) => setState(res.hasBusinessLink ? 'bound' : 'unbound'),
      () => setState('unbound')
    );
  }, [userStore.accountId]);

  async function handleBind() {
    setError('');
    setState('binding');
    try {
      const res = await NotificationApi.bindVk({
        phone: userStore.phone,
        accountId: userStore.accountId || undefined,
      });
      setBindCode(res.bindCode);
      setVkBotChatUrl(res.vkBotChatUrl);
      setShowModal(true);
    } catch {
      setError('Ошибка при создании кода привязки. Попробуйте позже.');
      setState('unbound');
    }
  }

  async function handleRefreshCode() {
    try {
      const res = await NotificationApi.bindVk({
        phone: userStore.phone,
        accountId: userStore.accountId || undefined,
      });
      setBindCode(res.bindCode);
      setVkBotChatUrl(res.vkBotChatUrl);
    } catch {
      setError('Ошибка при обновлении кода. Попробуйте позже.');
    }
  }

  async function handleUnbind() {
    setError('');
    setState('unbinding');
    try {
      await NotificationApi.unbindBusinessVk(userStore.accountId);
      const res = await NotificationApi.checkBusinessVkLink(
        userStore.accountId
      );
      if (res.hasBusinessLink) {
        setError('Аккаунт не отвязан, попробуйте еще раз');
        setState('bound');
      } else {
        setState('unbound');
      }
    } catch {
      setError('Ошибка при отвязке. Попробуйте позже.');
      setState('bound');
    }
  }

  function handleCloseModal() {
    setShowModal(false);
    setState('loading');
    NotificationApi.checkBusinessVkLink(userStore.accountId).then(
      (res) => setState(res.hasBusinessLink ? 'bound' : 'unbound'),
      () => setState('unbound')
    );
  }

  return (
    <>
      {state === 'loading' ? null : state === 'bound' ||
        state === 'unbinding' ? (
        <div>
          <DefaultButton
            buttonText="Отвязать ВК бота"
            onClick={handleUnbind}
            disabled={state === 'unbinding'}
            color="red"
            className={styles.vk_bind_button}
          />
          {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
        </div>
      ) : (
        <div>
          <DefaultButton
            buttonText="Подключить уведомления о записях в ВК"
            onClick={handleBind}
            disabled={state === 'binding'}
            className={styles.vk_bind_button}
          />
          {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
        </div>
      )}
      {showModal && (
        <ModalWindow onClickOutside={handleCloseModal}>
          <div
            className={styles.vk_bind__container}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.container__title}>
              <div className={styles.title__text}>
                Привязка к бизнес-боту VK
              </div>
              <button
                className={styles.title__close_button}
                onClick={handleCloseModal}
              >
                x
              </button>
            </div>
            <div className={styles.vk_section}>
              <p className={styles.bind_text}>
                Привяжите аккаунт ВКонтакте, чтобы получать уведомления о новых
                записях
              </p>
              <div className={styles.bind_code} onClick={handleCopyCode}>
                {bindCode}
              </div>
              <p className={styles.bind_hint}>
                Отправьте этот код боту ВКонтакте
              </p>
              <a
                href={vkBotChatUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.vk_button}
              >
                Написать боту
              </a>
              <div className={styles.bind_actions}>
                <DefaultButton
                  buttonText="Код отправлен"
                  color="green"
                  onClick={handleCloseModal}
                  className={styles.compact_button}
                />
                <DefaultButton
                  buttonText="Обновить код"
                  color="green"
                  onClick={handleRefreshCode}
                  className={styles.compact_button}
                />
              </div>
              {alertMessage && (
                <Alert
                  type="success"
                  message={alertMessage}
                  onClose={handleCloseAlert}
                />
              )}
            </div>
          </div>
        </ModalWindow>
      )}
    </>
  );
});
